import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  CONVERT_UNITS_MAP,
  MEASUREMENT_SYSTEM as MEASUREMENT_UNITS_SETTINGS,
} from 'src/utils/constants';
import { convertToUserUnits, formatUnits, getUnitsSettings } from 'src/utils/conversions';

const ConvertedUnits = ({
  quantity,
  units,
  addUnitsParentheses,
  className,
  skipTooltip,
  onlyTooltip,
  displayZero,
  useUserSystemUnit,
  dataCy,
}) => {
  if ((quantity === null || quantity === undefined || !units) && !displayZero) return null;

  const { measurementSystem, customVolumeUnit, customWeightUnit } = getUnitsSettings();

  const unitSystemComponents = {
    [MEASUREMENT_UNITS_SETTINGS.METRIC]: 'Metric',
    [MEASUREMENT_UNITS_SETTINGS.IMPERIAL]: 'Imperial',
    [MEASUREMENT_UNITS_SETTINGS.CUSTOM]: 'Custom',
  };

  const {
    quantity: convertedQuantity,
    units: convertedUnits,
    isConverted,
  } = convertToUserUnits(quantity, units, useUserSystemUnit);

  const displayCustomUnitSystem = () => {
    if (measurementSystem !== MEASUREMENT_UNITS_SETTINGS.CUSTOM) return null;
    return (
      <>
        <div>
          Volume Units: <strong>({formatUnits(customVolumeUnit)})</strong>
        </div>
        <div>
          Weight Units: <strong>({formatUnits(customWeightUnit)})</strong>
        </div>
      </>
    );
  };

  const displayUnitSystem = unitSystemComponents[measurementSystem] || 'Metric';

  // Function to render the units with or without parentheses
  const renderUnits = () => {
    const formattedUnits = formatUnits(convertedUnits);
    if (addUnitsParentheses) {
      return `(${formattedUnits})`;
    }
    return formattedUnits;
  };

  if (!isConverted) {
    return (
      <span className={className} data-cy={dataCy}>
        {/* Used converted units map here to change units to short forms. No conversion from two different
        unit actually occur here from users unit settings. */}
        {quantity || 0} {CONVERT_UNITS_MAP[units]}
      </span>
    );
  }

  if (skipTooltip) {
    return (
      <span className={className} data-cy={dataCy}>
        {convertedQuantity} {renderUnits()}
      </span>
    );
  }

  return (
    <OverlayTrigger
      flip
      placement='bottom'
      popperConfig={{
        modifiers: [
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['top'],
            },
          },
        ],
      }}
      overlay={
        <Tooltip id='converted-units-tooltip'>
          <p className='mb15'>
            The value and units are displayed according to your <strong>Measurement Units</strong>{' '}
            selection <strong>({displayUnitSystem})</strong>
          </p>

          <div>{displayCustomUnitSystem()}</div>

          <div className='mt10'>
            <div>
              Original Value: <strong>{quantity || 0}</strong>
            </div>
            <div>
              Original Units: <strong>({formatUnits(units)})</strong>
            </div>
          </div>
        </Tooltip>
      }
    >
      <span className={className} data-cy={dataCy}>
        {!onlyTooltip && (
          <>
            {convertedQuantity} {renderUnits()}
          </>
        )}
        <FontAwesomeIcon
          icon={faInfoCircle}
          color='#FFA500'
          className={!onlyTooltip && 'spacer-left'}
        />
      </span>
    </OverlayTrigger>
  );
};

ConvertedUnits.defaultProps = {
  addUnitsParentheses: false,
  className: '',
  skipTooltip: false,
  onlyTooltip: false,
  displayZero: false,
  useUserSystemUnit: false,
  dataCy: null,
};

ConvertedUnits.propTypes = {
  quantity: PropTypes.number.isRequired,
  units: PropTypes.string.isRequired,
  addUnitsParentheses: PropTypes.bool,
  className: PropTypes.string,
  skipTooltip: PropTypes.bool,
  onlyTooltip: PropTypes.bool,
  displayZero: PropTypes.bool,
  useUserSystemUnit: PropTypes.bool,
  dataCy: PropTypes.string,
};

export default ConvertedUnits;
